import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import ClientHomeRedirect from "@/components/layout/ClientHomeRedirect";
import LoginPage from "@/pages/LoginPage";
import { auth } from "@/lib/auth";
import Dashboard from "@/pages/Dashboard";
import Placeholder from "@/pages/Placeholder";
import CalendarPage from "@/pages/CalendarPage";
import TasksPage from "@/pages/TasksPage";
import EmployeeRequestsPage from "@/pages/EmployeeRequestsPage";
import BillingPage from "@/pages/BillingPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import NCRsPage from "@/pages/NCRsPage";
import ObjectivesPage from "@/pages/ObjectivesPage";
import SuppliersPage from "@/pages/SuppliersPage";
import EmployeesPage from "@/pages/EmployeesPage";
import CompetencyMatrixPage from "@/pages/CompetencyMatrixPage";
import TrainingsCatalogPage from "@/pages/TrainingsCatalogPage";
import DocumentsPage from "@/pages/DocumentsPage";
import AssistanceRequestsPage from "@/pages/AssistanceRequestsPage";
import AuditorAccessPage from "@/pages/AuditorAccessPage";
import AuditorEntryPage from "@/pages/AuditorEntryPage";
import CustomerSatisfactionPage from "@/pages/CustomerSatisfactionPage";
import PublicSurveyPage from "@/pages/PublicSurveyPage";
import EquipmentPage from "@/pages/EquipmentPage";
import RisksOpportunitiesPage from "@/pages/RisksOpportunitiesPage";
import AuditsReviewsPage from "@/pages/AuditsReviewsPage";
import ClientIsoTasksPage from "@/pages/ClientIsoTasksPage";
import LegalRequirementsPage from "@/pages/LegalRequirementsPage";
import AccountsPage from "@/pages/AccountsPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";

function App() {
  return (
    <div className="App ambient-bg">
      <BrowserRouter>
        <Routes>
          {/* Auditor one-time entry — outside shell */}
          <Route path="/auditor" element={<AuditorEntryPage />} />
          {/* Public customer survey — outside shell, no auth */}
          <Route path="/survey/:token" element={<PublicSurveyPage />} />
          {/* Public login — outside shell */}
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedShell />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ClientHomeRedirect />} />
            <Route path="/schedule" element={<CalendarPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/employee-requests" element={<EmployeeRequestsPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:slug" element={<ClientPortalPage />} />
            <Route path="/ncrs" element={<NCRsPage />} />
            <Route path="/objectives" element={<ObjectivesPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/employees" element={<EmployeesPage />} />
            <Route path="/competency-matrix" element={<CompetencyMatrixPage />} />
            <Route path="/trainings" element={<TrainingsCatalogPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/assistance" element={<AssistanceRequestsPage />} />
            <Route path="/auditor-access" element={<AuditorAccessPage />} />
            <Route path="/customer-satisfaction" element={<CustomerSatisfactionPage />} />
            <Route path="/equipment" element={<EquipmentPage />} />
            <Route path="/risks" element={<RisksOpportunitiesPage />} />
            <Route path="/audits-reviews" element={<AuditsReviewsPage />} />
            <Route path="/iso-tasks" element={<ClientIsoTasksPage />} />
            <Route path="/legal-requirements" element={<LegalRequirementsPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/requests" element={<Placeholder title="Client Requests" description="Respond to inbound client requests quickly." />} />
            <Route path="/deadlines" element={<Placeholder title="Compliance Deadlines" description="Never miss an ISO certification or audit deadline." />} />
            <Route path="/clients" element={<Placeholder title="Clients Directory" description="Client database will arrive in the next module." soon />} />
            <Route path="/audits" element={<Placeholder title="ISO Audits" description="Full audit workflows are coming soon." soon />} />
            <Route path="/settings" element={<Placeholder title="Portal Settings" description="Manage your workspace, team and preferences." />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

function ProtectedShell() {
  const location = useLocation();
  const [authed, setAuthed] = useState(auth.isAuthenticated());
  useEffect(() => {
    const sync = () => setAuthed(auth.isAuthenticated());
    window.addEventListener("isoquest:auth-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("isoquest:auth-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  if (!authed) return <Navigate to="/login" state={{ from: location }} replace />;
  return <AppShell />;
}

export default App;
